import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { VStack } from "../../../../styled-system/jsx";
import { useBulkUpdateDayparting } from "../../../modules/advertising/api/useBulkUpdateDayparting";
import { AdToolPanel } from "../AdToolPanel";
import { Button } from "../Chat";

const DAY_KEYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const AdDayPartingToolMessage = ({ adIds, dayPartingMultipliers, dayPartingMethod }) => {
  const { t } = useTranslation(["chat"]);
  const bulkUpdateDayparting = useBulkUpdateDayparting();
  const normalizedMethod = dayPartingMethod === undefined || dayPartingMethod === null ? 0 : Number(dayPartingMethod);
  const methodLabel =
    normalizedMethod === 1 ? t(($) => $.chat.dayparting["Enabled"])
    : normalizedMethod === 0 ? t(($) => $.chat.dayparting["Disabled"])
    : t(($) => $.chat.dayparting["Method {method}"], { method: normalizedMethod });
  const multiplierEntries = useMemo(() => {
    if (!dayPartingMultipliers) return [];
    if (Array.isArray(dayPartingMultipliers)) {
      return dayPartingMultipliers
        .filter((entry) => entry && typeof entry.key === "string")
        .map((entry) => ({
          key: entry.key,
          value: entry.value?.toString() ?? "",
        }));
    }
    if (typeof dayPartingMultipliers === "object") {
      return Object.entries(dayPartingMultipliers).map(([key, value]) => ({
        key,
        value: value?.toString?.() ?? "",
      }));
    }
    return [];
  }, [dayPartingMultipliers]);
  const multiplierMap = useMemo(() => {
    return multiplierEntries.reduce((acc, { key, value }) => {
      if (key) {
        const numericValue = Number.parseFloat(value);
        acc[key] = Number.isFinite(numericValue) ? numericValue : value;
      }
      return acc;
    }, {});
  }, [multiplierEntries]);
  const friendlyMultipliers = useMemo(() => {
    return multiplierEntries
      .map(({ key, value }) => {
        const [hourRaw, dayRaw] = key.split("-");
        const hour = Number(hourRaw);
        const dayIndex = Number(dayRaw);
        if (!Number.isFinite(hour) || !Number.isFinite(dayIndex) || dayIndex < 0 || dayIndex > 6) {
          return null;
        }
        const isPM = hour >= 12;
        let displayHour = hour % 12;
        if (displayHour === 0) displayHour = 12;
        const multiplierValue = Number.parseFloat(value);
        const formattedMultiplier = Number.isFinite(multiplierValue) ? `${multiplierValue}x` : `${value}x`;
        const dayName = t(($) => $.chat.dayparting.dayNames[DAY_KEYS[dayIndex]]);
        const description =
          isPM ?
            t(($) => $.chat.dayparting["At {hour} PM on {day}, apply a {multiplier} multiplier."], {
              hour: displayHour,
              day: dayName,
              multiplier: formattedMultiplier,
            })
          : t(($) => $.chat.dayparting["At {hour} AM on {day}, apply a {multiplier} multiplier."], {
              hour: displayHour,
              day: dayName,
              multiplier: formattedMultiplier,
            });
        return {
          key,
          description,
        };
      })
      .filter(Boolean);
  }, [multiplierEntries, t]);

  return (
    <AdToolPanel
      adIds={adIds}
      renderAfterList={() => (
        <div
          className={css({
            width: "full",
            backgroundColor: "#f7f7fa",
            borderRadius: "8px",
            padding: "12px",
            border: "1px solid #e2e2e5",
            fontSize: "12px",
            color: "#333",
            whiteSpace: "normal",
          })}
        >
          <div className={css({ fontSize: "13px", fontWeight: 600, marginBottom: "4px" })}>
            {t(($) => $.chat.dayparting["Dayparting status: {status}"], { status: methodLabel })}
          </div>
          {friendlyMultipliers.length > 0 ?
            <VStack
              gap={2}
              css={{
                maxHeight: "180px",
                overflowY: "auto",
                alignItems: "flex-start",
              }}
            >
              {friendlyMultipliers.map(({ key, description }) => (
                <div
                  key={key}
                  className={css({
                    fontSize: "12px",
                  })}
                >
                  {description}
                </div>
              ))}
            </VStack>
          : <div>{t(($) => $.chat.dayparting["No dayparting overrides provided."])}</div>}
        </div>
      )}
      renderActions={({ ads, isLoading }) => {
        const advertisementIds = (ads ?? []).map((ad) => Number(ad.ad_id)).filter((id) => Number.isFinite(id));
        const handleUpdateDayparting = () => {
          if (!advertisementIds.length || bulkUpdateDayparting.isPending) return;
          bulkUpdateDayparting.mutate({
            advertisement_ids: advertisementIds,
            day_parting_method: normalizedMethod,
            day_parting_multipliers: multiplierMap,
          });
        };
        const isButtonDisabled = bulkUpdateDayparting.isPending || isLoading || !advertisementIds.length;
        let buttonLabel = t(($) => $.chat.dayparting["Update Dayparting"]);
        if (bulkUpdateDayparting.isPending) {
          buttonLabel = t(($) => $.chat["Updating..."]);
        } else if (normalizedMethod === 1) {
          buttonLabel = t(($) => $.chat.dayparting["Apply Dayparting"]);
        } else if (normalizedMethod === 0) {
          buttonLabel = t(($) => $.chat.dayparting["Disable Dayparting"]);
        }

        return (
          <Button onClick={handleUpdateDayparting} disabled={isButtonDisabled}>
            {buttonLabel}
          </Button>
        );
      }}
    />
  );
};

export { AdDayPartingToolMessage };
